import React, { useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import PageHeader from "../../core/common/header/PageHeader";
import {
  FaBook,
  FaCheckCircle,
  FaClock,
  FaChartLine,
  FaTasks,
  FaQuestionCircle,
  FaFileAlt,
  FaUsers,
  FaGraduationCap,
  FaPen,
} from "react-icons/fa";
import {
  StudentProgressDTO,
  CourseProgressDTO,
} from "../../feature-module/interface/progress";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const StudentProgressDashboard: React.FC = () => {
  const [progressData, setProgressData] = useState<StudentProgressDTO | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const { student } = useAuth();

  useEffect(() => {
    fetchProgressData();
  }, []);

  const fetchProgressData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/api/student/progress`, {
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch progress: ${response.status}`);
      }

      const data: StudentProgressDTO = await response.json();
      setProgressData(data);
    } catch (err: any) {
      console.error("Failed to fetch progress data:", err);
      setError(err.message || "Failed to load progress data");
    } finally {
      setLoading(false);
    }
  };

  const getProgressBarColor = (percentage: number): string => {
    if (percentage >= 80) return "bg-emerald-500";
    if (percentage >= 60) return "bg-blue-500";
    if (percentage >= 40) return "bg-amber-500";
    if (percentage > 0) return "bg-rose-500";
    return "bg-gray-300";
  };

  const getProgressTextColor = (percentage: number): string => {
    if (percentage >= 80) return "text-emerald-600";
    if (percentage >= 60) return "text-blue-600";
    if (percentage >= 40) return "text-amber-600";
    return "text-rose-600";
  };

  const getCourseCardGradient = (
    courseType: string,
    overallProgress: number
  ): string => {
    if (courseType === "german_individual") {
      if (overallProgress >= 80)
        return "bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-200";
      if (overallProgress >= 60)
        return "bg-gradient-to-br from-blue-50 to-cyan-50 border-blue-200";
      if (overallProgress >= 40)
        return "bg-gradient-to-br from-amber-50 to-yellow-50 border-amber-200";
      return "bg-gradient-to-br from-rose-50 to-pink-50 border-rose-200";
    } else if (courseType === "combined") {
      return "bg-gradient-to-br from-gray-50 to-slate-50 border-gray-200";
    } else {
      return "bg-gradient-to-br from-gray-50 to-slate-50 border-gray-200";
    }
  };

  const getCourseHeaderColor = (courseType: string): string => {
    if (courseType === "german_individual") return "from-[#009fe3] to-cyan-500";
    if (courseType === "combined") return "from-gray-400 to-slate-400";
    return "from-gray-400 to-slate-400";
  };

  const renderProgressBar = (
    percentage: number,
    size: "small" | "medium" | "large" = "medium"
  ) => {
    const heightClass =
      size === "small" ? "h-3" : size === "large" ? "h-6" : "h-4";
    const bgClass = getProgressBarColor(percentage);

    return (
      <div
        className={`w-full ${heightClass} bg-gray-200 rounded-full overflow-hidden shadow-inner`}
      >
        <div
          className={`h-full ${bgClass} transition-all duration-700 ease-out shadow-sm`}
          style={{ width: `${Math.min(percentage, 100)}%` }}
        />
      </div>
    );
  };

  const renderGermanCourseProgress = (course: CourseProgressDTO) => {
    if (!course.components) return null;

    const components = [
      {
        name: "Chapters",
        data: course.components.chapters,
        icon: FaBook,
        bgColor: "bg-sky-50",
        borderColor: "border-sky-200",
        iconColor: "text-sky-600",
        textColor: "text-sky-700",
        progressColor: "bg-sky-500",
        progressBg: "bg-sky-200",
      },
      {
        name: "Quizzes",
        data: course.components.quizzes,
        icon: FaQuestionCircle,
        bgColor: "bg-sky-50",
        borderColor: "border-sky-200",
        iconColor: "text-sky-600",
        textColor: "text-sky-700",
        progressColor: "bg-sky-500",
        progressBg: "bg-sky-200",
      },
      {
        name: "Homework",
        data: course.components.homeworks,
        icon: FaFileAlt,
        bgColor: "bg-cyan-50",
        borderColor: "border-cyan-200",
        iconColor: "text-cyan-600",
        textColor: "text-cyan-700",
        progressColor: "bg-cyan-500",
        progressBg: "bg-cyan-200",
      },
      {
        name: "Writing",
        data: course.components.writingTasks,
        icon: FaPen,
        bgColor: "bg-purple-50",
        borderColor: "border-purple-200",
        iconColor: "text-purple-600",
        textColor: "text-purple-700",
        progressColor: "bg-purple-500",
        progressBg: "bg-purple-200",
      },
      {
        name: "Classes",
        data: course.components.classes,
        icon: FaUsers,
        bgColor: "bg-amber-50",
        borderColor: "border-amber-200",
        iconColor: "text-amber-600",
        textColor: "text-amber-700",
        progressColor: "bg-amber-500",
        progressBg: "bg-amber-200",
      },
      {
        name: "Exam",
        data: course.components.exam,
        icon: FaGraduationCap,
        bgColor: "bg-emerald-50",
        borderColor: "border-emerald-200",
        iconColor: "text-emerald-600",
        textColor: "text-emerald-700",
        progressColor: "bg-emerald-500",
        progressBg: "bg-emerald-200",
      },
    ];

    return (
      <div
        key={course.courseId}
        className={`rounded-xl shadow-lg border p-6 mb-6 ${getCourseCardGradient(
          "german_individual",
          course.overallProgress
        )}`}
      >
        {/* Course Header */}
        <div
          className={`bg-gradient-to-r ${getCourseHeaderColor(
            "german_individual"
          )} text-white p-4 rounded-lg mb-6`}
        >
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div>
              <h3 className="text-xl font-bold flex items-center gap-2">
                <FaBook />
                {course.courseTitle}
              </h3>
              {course.batchName && (
                <p className="text-cyan-100 text-sm mt-1">
                  Batch: {course.batchName}
                </p>
              )}
            </div>
            <div className="mt-4 md:mt-0 text-center">
              <div className="text-3xl font-bold mb-1">
                {course.overallProgress.toFixed(1)}%
              </div>
              <div className="text-cyan-100 text-sm">Overall Progress</div>
            </div>
          </div>
        </div>

        {/* Overall Progress Bar */}
        <div className="mb-6 bg-white rounded-lg p-4 shadow-sm">
          <div className="flex justify-between items-center mb-3">
            <span className="text-sm font-semibold text-gray-700">
              Course Completion
            </span>
            <span
              className={`text-sm font-bold ${getProgressTextColor(
                course.overallProgress
              )}`}
            >
              {course.overallProgress.toFixed(1)}%
            </span>
          </div>
          {renderProgressBar(course.overallProgress, "large")}
        </div>

        {/* Components Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
          {components.map((component) => {
            const Icon = component.icon;
            return (
              <div
                key={component.name}
                className={`${component.bgColor} ${component.borderColor} border rounded-lg p-4 hover:shadow-md transition-shadow duration-200`}
              >
                <div className="flex items-center justify-between mb-3">
                  <Icon className={`text-lg ${component.iconColor}`} />
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    {component.name}
                  </span>
                </div>

                <div className="text-center mb-3">
                  <div className={`text-2xl font-bold ${component.textColor}`}>
                    {component.data.completed}/{component.data.total}
                  </div>
                  <div
                    className={`text-sm font-semibold ${component.textColor}`}
                  >
                    {component.data.percentage.toFixed(1)}%
                  </div>
                </div>

                <div
                  className={`w-full h-3 ${component.progressBg} rounded-full overflow-hidden`}
                >
                  <div
                    className={`h-full ${component.progressColor} transition-all duration-300`}
                    style={{ width: `${component.data.percentage}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderOtherCourseProgress = (course: CourseProgressDTO) => {
    if (!course.additionalSteps) return null;

    return (
      <div
        key={course.courseId}
        className={`rounded-xl shadow-lg border p-6 mb-6 ${getCourseCardGradient(
          "other",
          course.overallProgress
        )}`}
      >
        {/* Course Header */}
        <div
          className={`bg-gradient-to-r ${getCourseHeaderColor(
            "other"
          )} text-white p-4 rounded-lg mb-6`}
        >
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div>
              <h3 className="text-xl font-bold flex items-center gap-2">
                <FaTasks />
                {course.courseTitle}
              </h3>
              <p className="text-gray-100 text-sm mt-1">
                {course.additionalSteps.completed} of{" "}
                {course.additionalSteps.total} steps completed
              </p>
            </div>
            <div className="mt-4 md:mt-0 text-center">
              <div className="text-3xl font-bold mb-1">
                {course.overallProgress.toFixed(1)}%
              </div>
              <div className="text-gray-100 text-sm">Overall Progress</div>
            </div>
          </div>
        </div>

        {/* Overall Progress Bar */}
        <div className="mb-6 bg-white rounded-lg p-4 shadow-sm">
          <div className="flex justify-between items-center mb-3">
            <span className="text-sm font-semibold text-gray-700">
              Course Completion
            </span>
            <span
              className={`text-sm font-bold ${getProgressTextColor(
                course.overallProgress
              )}`}
            >
              {course.overallProgress.toFixed(1)}%
            </span>
          </div>
          {renderProgressBar(course.overallProgress, "large")}
        </div>

        {/* Steps List */}
        <div className="bg-white rounded-lg p-4 shadow-sm">
          <h4 className="font-semibold text-gray-800 mb-4 flex items-center">
            <FaTasks className="mr-2 text-gray-600" />
            Course Steps
          </h4>
          <div className="space-y-3">
            {course.additionalSteps.stepDetails.map((step, index) => (
              <div
                key={index}
                className={`flex items-center justify-between p-3 rounded-lg border ${
                  step.isCompleted
                    ? "bg-emerald-50 border-emerald-200"
                    : "bg-gray-50 border-gray-200"
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0">
                    {step.isCompleted ? (
                      <FaCheckCircle className="text-emerald-500 text-lg" />
                    ) : (
                      <FaClock className="text-gray-400 text-lg" />
                    )}
                  </div>
                  <div>
                    <div className="font-medium text-gray-800">
                      {step.stepName}
                    </div>
                    {step.completedAt && (
                      <div className="text-xs text-gray-600">
                        Completed on{" "}
                        {new Date(step.completedAt).toLocaleDateString()}
                        {step.completedBy && ` by ${step.completedBy}`}
                      </div>
                    )}
                    {step.notes && (
                      <div className="text-xs text-gray-500 mt-1">
                        {step.notes}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex-shrink-0">
                  {step.isCompleted ? (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800">
                      Complete
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-600">
                      Pending
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderCombinedCourseProgress = (course: CourseProgressDTO) => {
    return (
      <div
        key={course.courseId}
        className={`rounded-xl shadow-lg border p-6 mb-6 ${getCourseCardGradient(
          "combined",
          course.overallProgress
        )}`}
      >
        {/* Course Header */}
        <div
          className={`bg-gradient-to-r ${getCourseHeaderColor(
            "combined"
          )} text-white p-4 rounded-lg mb-6`}
        >
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div>
              <h3 className="text-xl font-bold flex items-center gap-2">
                <FaChartLine />
                {course.courseTitle}
              </h3>
              <p className="text-gray-100 text-sm mt-1">
                Complete German + Ausbildung Preparation
              </p>
            </div>
            <div className="mt-4 md:mt-0 text-center">
              <div className="text-3xl font-bold mb-1">
                {course.overallProgress.toFixed(1)}%
              </div>
              <div className="text-gray-100 text-sm">Overall Progress</div>
            </div>
          </div>
        </div>

        {/* Overall Progress Bar */}
        <div className="mb-6 bg-white rounded-lg p-4 shadow-sm">
          <div className="flex justify-between items-center mb-3">
            <span className="text-sm font-semibold text-gray-700">
              Course Completion
            </span>
            <span
              className={`text-sm font-bold ${getProgressTextColor(
                course.overallProgress
              )}`}
            >
              {course.overallProgress.toFixed(1)}%
            </span>
          </div>
          {renderProgressBar(course.overallProgress, "large")}
        </div>

        {/* Progress Breakdown */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* German Language Section */}
          <div className="bg-gradient-to-br from-blue-50 to-cyan-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-semibold text-gray-800 mb-4 flex items-center">
              <FaBook className="mr-2 text-blue-600" />
              German Language (45%)
            </h4>
            {course.levels && course.levels.length > 0 ? (
              <div className="space-y-4">
                {course.levels.map((level, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-lg p-4 border border-blue-100 shadow-sm"
                  >
                    <div className="flex justify-between items-center mb-3">
                      <span className="font-semibold text-blue-800">
                        Level {level.level}
                      </span>
                      <span
                        className={`text-sm font-bold ${getProgressTextColor(
                          level.progress
                        )}`}
                      >
                        {level.progress.toFixed(1)}%
                      </span>
                    </div>
                    {renderProgressBar(level.progress, "small")}

                    {level.components && (
                      <div className="grid grid-cols-6 gap-2 mt-3 text-xs">
                        <div className="text-center bg-sky-50 rounded p-1">
                          <div className="font-medium text-sky-700">Ch</div>
                          <div className="text-sky-600">
                            {level.components.chapters.completed}/14
                          </div>
                        </div>
                        <div className="text-center bg-sky-50 rounded p-1">
                          <div className="font-medium text-sky-700">Qz</div>
                          <div className="text-sky-600">
                            {level.components.quizzes.completed}/14
                          </div>
                        </div>
                        <div className="text-center bg-cyan-50 rounded p-1">
                          <div className="font-medium text-cyan-700">Hw</div>
                          <div className="text-cyan-600">
                            {level.components.homeworks.completed}/14
                          </div>
                        </div>
                        <div className="text-center bg-purple-50 rounded p-1">
                          <div className="font-medium text-purple-700">Wr</div>
                          <div className="text-purple-600">
                            {level.components.writingTasks.completed}/14
                          </div>
                        </div>
                        <div className="text-center bg-amber-50 rounded p-1">
                          <div className="font-medium text-amber-700">Cl</div>
                          <div className="text-amber-600">
                            {level.components.classes.completed}/
                            {level.components.classes.total}
                          </div>
                        </div>
                        <div className="text-center bg-emerald-50 rounded p-1">
                          <div className="font-medium text-emerald-700">Ex</div>
                          <div className="text-emerald-600">
                            {level.components.exam.completed}/1
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-sm bg-white rounded p-3">
                No German language progress yet
              </p>
            )}
          </div>

          {/* Ausbildung Steps Section */}
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4">
            <h4 className="font-semibold text-gray-800 mb-4 flex items-center">
              <FaTasks className="mr-2 text-green-600" />
              Ausbildung Steps (55%)
            </h4>
            {course.additionalSteps ? (
              <div className="bg-white rounded-lg p-4 border border-green-100 shadow-sm">
                <div className="flex justify-between items-center mb-3">
                  <span className="text-sm font-medium text-gray-700">
                    {course.additionalSteps.completed} of{" "}
                    {course.additionalSteps.total} completed
                  </span>
                  <span
                    className={`text-sm font-bold ${getProgressTextColor(
                      course.additionalSteps.percentage
                    )}`}
                  >
                    {course.additionalSteps.percentage.toFixed(1)}%
                  </span>
                </div>
                {renderProgressBar(course.additionalSteps.percentage, "small")}

                <div className="mt-4 max-h-40 overflow-y-auto space-y-2">
                  {course.additionalSteps.stepDetails
                    .slice(0, 5)
                    .map((step, index) => (
                      <div
                        key={index}
                        className={`flex items-center text-xs p-2 rounded ${
                          step.isCompleted ? "bg-emerald-50" : "bg-gray-50"
                        }`}
                      >
                        {step.isCompleted ? (
                          <FaCheckCircle className="text-emerald-500 mr-2 flex-shrink-0" />
                        ) : (
                          <FaClock className="text-gray-400 mr-2 flex-shrink-0" />
                        )}
                        <span
                          className={
                            step.isCompleted
                              ? "text-emerald-700 font-medium"
                              : "text-gray-600"
                          }
                        >
                          {step.stepName}
                        </span>
                      </div>
                    ))}
                  {course.additionalSteps.stepDetails.length > 5 && (
                    <div className="text-xs text-gray-500 text-center p-2 bg-gray-50 rounded">
                      +{course.additionalSteps.stepDetails.length - 5} more
                      steps
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-gray-500 text-sm bg-white rounded p-3">
                No preparatory steps yet
              </p>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#009fe3] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your progress...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <p className="text-rose-600 mb-4">{error}</p>
          <button
            onClick={fetchProgressData}
            className="px-4 py-2 bg-[#009fe3] text-white rounded hover:bg-cyan-600 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <PageHeader
        icon={<FaChartLine />}
        title="Course Progress"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Dashboard", to: "/student/student-dashboard" },
          { label: "Progress" },
        ]}
      />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-10">
        {/* Welcome Section */}
        <div className="mb-10 bg-gradient-to-r from-[#009fe3] to-cyan-500 text-white rounded-xl p-6 shadow-lg">
          <h2 className="text-3xl font-bold mb-2 flex items-center gap-2 text-white">
            <FaChartLine />
            {progressData?.studentName
              ? `Welcome, ${progressData.studentName.split(" ")[0]} 👋`
              : "Welcome 👋"}
          </h2>
          <p className="text-white text-base opacity-90">
            Track your progress across all enrolled courses and see how you're
            doing.
          </p>
        </div>

        {!progressData || progressData.courses.length === 0 ? (
          <div className="text-center text-gray-500 py-10 bg-white rounded-xl shadow-lg">
            <FaBook className="text-6xl text-gray-300 mx-auto mb-4" />
            <p className="text-lg font-medium text-gray-700">
              No course progress data available yet.
            </p>
            <p className="text-sm mt-2 text-gray-500">
              Once you're enrolled in courses and start learning, your progress
              will appear here.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {progressData.courses.map((course) => {
              if (course.courseType === "german_individual") {
                return renderGermanCourseProgress(course);
              } else if (course.courseType === "combined") {
                return renderCombinedCourseProgress(course);
              } else {
                return renderOtherCourseProgress(course);
              }
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default StudentProgressDashboard;
